import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from "reactstrap";
// Using custom Bootstrap pagination for consistency
import { getSalaryAdvances, saveSalaryAdvance } from "../../components/Api/GmsSalaryAdvance";
import { getEmployees } from "../../components/Api/GmsEmployee"; // Keep this import
import { Eye, SquarePen, CheckCircle } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin,
  hasApprovePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";

const defaultForm = {
  Advance_Id: 0,
  Employee_Id: null,
  Request_Date: new Date().toISOString().split('T')[0],
  Request_Amount: "",
  Approved_Date: null,
  Approved_Amount: null,
  Monthly_Deduction: null,
  Is_AutoDeduction: false,
  CreatedDate: new Date().toISOString(),
  CreatedBy: Number(localStorage.getItem("userId")) || 1,
  ModifiedDate: new Date().toISOString(),
  ModifiedBy: Number(localStorage.getItem("userId")) || 1,
  ApprovedBy: null,
  isApproved: false
};

const emptyFilter = {
  Advance_Id: null,
  Employee_Id: null,
  Request_Date: null,
  Approved_Date: null
};

export default function SalaryAdvancePage() {
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [approveLoadingRow, setApproveLoadingRow] = useState(null);
  const [approveModal, setApproveModal] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSalaryAdvance');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSalaryAdvance');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSalaryAdvance');
  const hasApprovePermission = hasApprovePermissionWithAdmin(permissions, '/GmsSalaryAdvance');
  const isAdmin = isUserAdmin();

  // Helper function to determine if an advance is effectively pending
const isAdvancePending = (advance) => {
  return !advance.approved_Date || advance.approved_Date === null;
};



  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "serialNumber",
        enableColumnFilter: false,
        cell: ({ row }) => row.index + 1,
      },
      {
        header: "Employee",
        accessorKey: "employeeName",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const advance = row.original;
          const employee = employees.find(e => String(e.employee_Id) === String(advance.employee_Id));
          return employee ? employee.employee_NameEn : `ID: ${advance.employee_Id}`;
        },
      },
      {
        header: "Request Date",
        accessorKey: "request_Date",
        enableColumnFilter: false,
        cell: ({ row }) => formatDate(row.original.request_Date),
      },
      {
        header: "Request Amount",
        accessorKey: "request_Amount",
        enableColumnFilter: false,
        cell: ({ row }) => formatCurrency(row.original.request_Amount),
      },
      {
  header: "Status",
  accessorKey: "status",
  enableColumnFilter: false,
  cell: ({ row }) => {
    const advance = row.original;
    const isPending = isAdvancePending(advance);
    return (
      <span className={`badge ${isPending ? 'bg-warning text-dark' : 'bg-success text-dark'}`}>
        {isPending ? 'Pending' : 'Approved'}
      </span>
    );
  },
},
      {
        header: "Approved Date",
        accessorKey: "approved_Date",
        enableColumnFilter: false,
        cell: ({ row }) => isAdvancePending(row.original) ? "-" : formatDate(row.original.approved_Date),
      },
      {
        header: "Approved Amount",
        accessorKey: "approved_Amount",
        enableColumnFilter: false,
        cell: ({ row }) => isAdvancePending(row.original) ? "-" : formatCurrency(row.original.approved_Amount),
      },
      {
        header: "Monthly Deduction",
        accessorKey: "monthly_Deduction",
        enableColumnFilter: false,
        cell: ({ row }) => isAdvancePending(row.original) ? "-" : formatCurrency(row.original.monthly_Deduction),
      },
      {
  header: "Auto Deduction",
  accessorKey: "is_AutoDeduction",
  enableColumnFilter: false,
  cell: ({ row }) => {
    const advance = row.original;
    const isPending = isAdvancePending(advance);
    
    if (isPending) {
      return <span className="badge bg-secondary text-dark">Not Set</span>;
    }
    
    if (advance.is_AutoDeduction === null || advance.is_AutoDeduction === undefined) {
      return <span className="badge bg-secondary text-dark">No</span>;
    }
    
    return advance.is_AutoDeduction ? (
      <span className="badge bg-success text-dark">Yes</span>
    ) : (
      <span className="badge bg-secondary text-dark">No</span>
    );
  },
},

      {
        header: "View",
        accessorKey: "view",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleView(row.original);
            }}
            className="btn btn-link p-0"
            disabled={viewLoadingRow === row.original.advance_Id}
          >
            {viewLoadingRow === row.original.advance_Id ? (
              <ClipLoader size={20} color="black" />
            ) : (
              <Eye size={20} color="black" />
            )}
          </button>
        ),
      },
      {
        header: "Approve",
        accessorKey: "approve",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const advance = row.original;
          // Only show approve button if user has approve permission
          if (!hasApprovePermission) {
            return <span className="text-muted">-</span>;
          }
          // Hide approve button if already approved
          return !isAdvancePending(advance) ? (
            <span className="text-muted">Approved</span>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleApprove(advance);
              }}
              className="btn btn-link p-0"
              disabled={approveLoadingRow === advance.advance_Id}
            >
              {approveLoadingRow === advance.advance_Id ? (
                <ClipLoader size={20} color="black" />
              ) : (
                <CheckCircle size={20} color="green" />
              )}
            </button>
          );
        },
      },
    ],
    [viewLoadingRow, approveLoadingRow, employees, hasApprovePermission]
  );

  useEffect(() => {
    if (hasPermission) {
      loadAdvances();
      loadEmployees();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

const loadAdvances = async (filterData = emptyFilter) => {
  setLoading(true);
  try {
    const data = await getSalaryAdvances(filterData);
    setAdvances(data);
    setTotalCount(data.length);
  } catch (error) {
    console.error("Error loading advances:", error);
    Swal.fire("Error", "Failed to load salary advances", "error");
  } finally {
    setLoading(false);
  }
};

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({});
      setEmployees(data);
    } catch (error) {
      Swal.fire("Error", "Failed to load employees: " + error.message, "error");
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validate form
      if (!form.Employee_Id) {
        throw new Error("Employee is required");
      }
      if (!form.Request_Date) {
        throw new Error("Request Date is required");
      }
      if (!form.Request_Amount || parseFloat(form.Request_Amount) <= 0) {
        throw new Error("Request Amount must be greater than 0");
      }

      // If approving, validate approval fields
      if (form.isApproved) {
        if (!form.Approved_Date) {
          throw new Error("Approved Date is required for approval");
        }
        if (!form.Approved_Amount || parseFloat(form.Approved_Amount) <= 0) {
          throw new Error("Approved Amount must be greater than 0");
        }
        if (!form.Monthly_Deduction || parseFloat(form.Monthly_Deduction) <= 0) {
          throw new Error("Monthly Deduction must be greater than 0");
        }
      }

      const result = await saveSalaryAdvance({
        ...form,
        Advance_Id: form.Advance_Id === 0 ? null : form.Advance_Id,
        ModifiedDate: new Date().toISOString(),
        ModifiedBy: Number(localStorage.getItem("userId")) || 1,
        CreatedBy: form.Advance_Id === 0 ? (Number(localStorage.getItem("userId")) || 1) : form.CreatedBy,
        // Ensure these fields are null for new requests
        Approved_Date: form.isApproved ? form.Approved_Date : null,
        Approved_Amount: form.isApproved ? form.Approved_Amount : null,
        Monthly_Deduction: form.isApproved ? form.Monthly_Deduction : null,
        Is_AutoDeduction: form.isApproved ? form.Is_AutoDeduction : false,
        ApprovedBy: form.isApproved ? (Number(localStorage.getItem("userId")) || 1) : null
      });

      Swal.fire("Success", "Salary advance saved successfully", "success");
      setForm(defaultForm);
      loadAdvances(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save salary advance: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveSubmit = async () => {
    if (!selectedAdvance) return;

    setLoading(true);
    try {
      // Validate approval form
      if (!form.Approved_Date) {
        throw new Error("Approved Date is required");
      }
      if (!form.Approved_Amount || parseFloat(form.Approved_Amount) <= 0) {
        throw new Error("Approved Amount must be greater than 0");
      }
      
      // Monthly Deduction validation - only required if Auto Deduction is enabled
      if (form.Is_AutoDeduction) {
        if (!form.Monthly_Deduction || parseFloat(form.Monthly_Deduction) <= 0) {
          throw new Error("Monthly Deduction must be greater than 0 when Auto Deduction is enabled");
        }
        if (parseFloat(form.Monthly_Deduction) > parseFloat(form.Approved_Amount)) {
          throw new Error("Monthly Deduction cannot be greater than Approved Amount");
        }
      }

      const result = await saveSalaryAdvance({
        ...selectedAdvance,
        Advance_Id: selectedAdvance.advance_Id,
        Employee_Id: selectedAdvance.employee_Id,
        Request_Date: selectedAdvance.request_Date,
        Request_Amount: selectedAdvance.request_Amount,
        Approved_Date: form.Approved_Date,
        Approved_Amount: form.Approved_Amount,
        Monthly_Deduction: form.Monthly_Deduction,
        Is_AutoDeduction: form.Is_AutoDeduction,
        ApprovedBy: Number(localStorage.getItem("userId")) || 1,
        ModifiedDate: new Date().toISOString(),
        ModifiedBy: Number(localStorage.getItem("userId")) || 1,
        isApproved: true
      });

      Swal.fire("Success", "Salary advance approved successfully", "success");
      setApproveModal(false);
      setSelectedAdvance(null);
      setForm(defaultForm);
      loadAdvances(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to approve salary advance: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (advance) => {
    setViewLoadingRow(advance.advance_Id);
    try {
      // Find employee name and code
      const employee = employees.find(e => String(e.employee_Id) === String(advance.employee_Id));
      const employeeName = employee ? employee.employee_NameEn : `ID: ${advance.employee_Id}`;
      const employeeCode = employee ? employee.employee_Code : 'N/A';

      Swal.fire({
        title: "Salary Advance Details",
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Employee:</strong> ${employeeName}</p>
              <p><strong>Employee Code:</strong> ${employeeCode}</p>
              <p><strong>Request Date:</strong> ${formatDate(advance.request_Date)}</p>
              <p><strong>Request Amount:</strong> ${formatCurrency(advance.request_Amount)}</p>
              <p><strong>Status:</strong> ${advance.approved_Date ? 'Approved' : 'Pending'}</p>
            </div>
            ${advance.approved_Date ? `
              <div class="mb-3">
                <h6 class="mb-2">Approval Information</h6>
                <p><strong>Approved Date:</strong> ${formatDate(advance.approved_Date)}</p>
                <p><strong>Approved Amount:</strong> ${formatCurrency(advance.approved_Amount)}</p>
                <p><strong>Monthly Deduction:</strong> ${formatCurrency(advance.monthly_Deduction)}</p>
                <p><strong>Auto Deduction:</strong> ${advance.is_AutoDeduction ? 'Yes' : 'No'}</p>
              </div>
            ` : ''}
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
      Swal.fire("Error", "Failed to view salary advance details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleApprove = (advance) => {
    // Don't allow approving if already approved
    if (advance.approved_Date) {
      Swal.fire("Already Approved", "This salary advance is already approved", "info");
      return;
    }

    setSelectedAdvance(advance);
    setForm({
      ...defaultForm,
      Approved_Date: new Date().toISOString().split('T')[0],
      Approved_Amount: advance.request_Amount,
      Monthly_Deduction: "",
      Is_AutoDeduction: false,
    });
    setApproveModal(true);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadAdvances(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadAdvances(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data for display
  const getPaginatedAdvances = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return advances.slice(startIndex, endIndex);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    // Additional check for min date string before formatting
    if (typeof dateString === 'string' && dateString.startsWith("0001-01-01")) {
      return "-";
    }
    return new Date(dateString).toLocaleDateString('en-GB'); // Using 'en-GB' for dd/mm/yyyy
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Options for searchable dropdowns
  const employeeOptions = useMemo(() => 
    employees.map(emp => ({
      value: emp.employee_Id,
      label: emp.employee_NameEn
    }))
  , [employees]);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Payroll" breadcrumbItem="Salary Advance" />
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {/* Form Section - Only show if user has edit permission */}
                {hasEditOrHigher && (
                  <Card className="mb-4">
                    <CardBody>
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Employee *</Label>
                            <Select
                              value={employeeOptions.find(
                                option => option.value === form.Employee_Id
                              )}
                              onChange={(option) =>
                                handleFormChange("Employee_Id", option ? option.value : null)
                              }
                              options={employeeOptions}
                              placeholder="Select Employee..."
                              isDisabled={form.Advance_Id !== 0}
                              required
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Request Date *</Label>
                            <Input
                              type="date"
                              name="Request_Date"
                              value={form.Request_Date}
                              onChange={(e) => handleFormChange("Request_Date", e.target.value)}
                              required
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Request Amount *</Label>
                            <Input
                              type="number"
                              name="Request_Amount"
                              value={form.Request_Amount}
                              onChange={(e) => handleFormChange("Request_Amount", e.target.value)}
                              placeholder="Enter amount"
                              min="0"
                              step="0.01"
                              required
                            />
                          </div>
                        </Col>
                      </Row>

                      <div className="d-flex gap-2">
                        <Button
                          type="submit"
                          color="success"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <ClipLoader size={20} color="white" />{" "}
                              <span className="ms-2">Saving...</span>
                            </>
                          ) : (
                            "Save Request"
                          )}
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
                )}

                {/* Filter Section */}
                <Card className="mb-4">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">Filter Salary Advances</h5>
                      <Button
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>

                    {showFilters && (
                      <>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Employee</Label>
                              <Select
                                isClearable
                                value={
                                  filter.Employee_Id
                                    ? employeeOptions.find(
                                        option => option.value === filter.Employee_Id
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "Employee_Id",
                                    option ? option.value : null
                                  )
                                }
                                options={employeeOptions}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Request Date</Label>
                              <Input
                                type="date"
                                name="Request_Date"
                                value={filter.Request_Date || ""}
                                onChange={(e) => handleFilterChange("Request_Date", e.target.value)}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Approved Date</Label>
                              <Input
                                type="date"
                                name="Approved_Date"
                                value={filter.Approved_Date || ""}
                                onChange={(e) => handleFilterChange("Approved_Date", e.target.value)}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Status</Label>
                              <Select
                                isClearable
                                value={
                                  filter.status
                                    ? { value: filter.status, label: filter.status }
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "status",
                                    option ? option.value : null
                                  )
                                }
                                options={[
                                  { value: "Approved", label: "Approved" },
                                  { value: "Pending", label: "Pending" }
                                ]}
                              />
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply
                          </Button>
                          <Button color="warning" onClick={handleClearFilter}>
                            Clear
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Salary Advance List ({totalCount} records)
                    </h5>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Employee</th>
                            <th>Request Date</th>
                            <th>Request Amount</th>
                            <th>Approved Date</th>
                            <th>Approved Amount</th>
                            <th>Monthly Deduction</th>
                            <th>Auto Deduction</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {advances.length === 0 ? (
                            <tr>
                              <td colSpan="10" className="text-center">
                                No salary advances found
                              </td>
                            </tr>
                          ) : (
                            getPaginatedAdvances().map((advance, index) => (
                              <tr key={advance.advance_Id}>
                                <td>{(page - 1) * pageSize + index + 1}</td>
                                <td>{employees.find(e => e.employee_Id === advance.employee_Id)?.employee_NameEn || `ID: ${advance.employee_Id}`}</td>
                                <td>{formatDate(advance.request_Date)}</td>
                                <td className="text-end">{formatCurrency(advance.request_Amount)}</td>
                                <td>{advance.approved_Date ? formatDate(advance.approved_Date) : '-'}</td>
                                <td className="text-end">{advance.approved_Amount ? formatCurrency(advance.approved_Amount) : '-'}</td>
                                <td className="text-end">{advance.monthly_Deduction ? formatCurrency(advance.monthly_Deduction) : '-'}</td>
                                <td>
                                  <span className={`badge ${advance.is_AutoDeduction ? 'bg-success' : 'bg-secondary'}`}>
                                    {advance.is_AutoDeduction ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${isAdvancePending(advance) ? 'bg-warning' : 'bg-success'}`}>
                                    {isAdvancePending(advance) ? 'Pending' : 'Approved'}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => handleView(advance)}
                                    >
                                      View
                                    </Button>
                                    {isAdvancePending(advance) && hasApprovePermission && (
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => handleApprove(advance)}
                                      >
                                        Approve
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                      </div>
                      {totalCount > pageSize && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>
                          
                          {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;
                            
                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(totalCount / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Approval Modal */}
        <Modal isOpen={approveModal} toggle={() => setApproveModal(!approveModal)}>
          <ModalHeader toggle={() => setApproveModal(!approveModal)}>
            Approve Salary Advance
          </ModalHeader>
          <ModalBody>
            {selectedAdvance && (
              <Form>
                <Row>
                  <Col md={12}>
                    <div className="mb-3">
                      <Label>Employee</Label>
                      <Input
                        type="text"
                        value={employees.find(e => e.employee_Id === selectedAdvance.employee_Id)?.employee_NameEn || `ID: ${selectedAdvance.employee_Id}`}
                        disabled
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Request Date</Label>
                      <Input
                        type="text"
                        value={formatDate(selectedAdvance.request_Date)}
                        disabled={form.Advance_Id !== 0}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Request Amount</Label>
                      <Input
                        type="text"
                        value={formatCurrency(selectedAdvance.request_Amount)}
                        disabled
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Approved Date *</Label>
                      <Input
                        type="date"
                        name="Approved_Date"
                        value={form.Approved_Date}
                        onChange={(e) => handleFormChange("Approved_Date", e.target.value)}
                        required
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Approved Amount *</Label>
                      <Input
                        type="number"
                        name="Approved_Amount"
                        value={form.Approved_Amount}
                        onChange={(e) => handleFormChange("Approved_Amount", e.target.value)}
                        placeholder="Enter approved amount"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Monthly Deduction {form.Is_AutoDeduction ? '*' : ''}</Label>
                      <Input
                        type="number"
                        name="Monthly_Deduction"
                        value={form.Monthly_Deduction}
                        onChange={(e) => handleFormChange("Monthly_Deduction", e.target.value)}
                        placeholder="Enter monthly deduction"
                        min="0"
                        step="0.01"
                        required={form.Is_AutoDeduction}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-check mt-4">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="Is_AutoDeduction"
                        name="Is_AutoDeduction"
                        checked={form.Is_AutoDeduction}
                        onChange={(e) => handleFormChange("Is_AutoDeduction", e.target.checked)}
                      />
                      <Label className="form-check-label" for="Is_AutoDeduction">
                        Auto Deduction
                      </Label>
                    </div>
                  </Col>
                </Row>
              </Form>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setApproveModal(false)}>
              Cancel
            </Button>
            <Button color="success" onClick={handleApproveSubmit} disabled={loading}>
              {loading ? (
                <>
                  <ClipLoader size={20} color="white" />{" "}
                  <span className="ms-2">Processing...</span>
                </>
              ) : (
                "Approve"
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
